

import React, { useState, useEffect, useContext, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Switch,
  Typography,
  Divider,
  Box,
  Alert,
  CircularProgress
} from '@mui/material';
import { Settings as SettingsIcon } from '@mui/icons-material';
import { MyContext } from '../../App';
import { fetchDataFromApi, editData } from '../../utils/api';

const PASSWORD = 'Rismic2008@#';

const AdminSettings = ({ open, onClose }) => {
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const context = useContext(MyContext);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setShowPasswordDialog(true);
      setSettingsLoaded(false);
      setPasswordInput('');
      setPasswordError('');
      setSettings({});
      setError('');
    }
  }, [open]);

  const verifyPassword = () => {
    if (passwordInput === PASSWORD) {
      setShowPasswordDialog(false);
      setPasswordError('');
      fetchSettings();
    } else {
      setPasswordError('Incorrect password. Please try again.');
      setPasswordInput('');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      verifyPassword();
    } else if (e.key === 'Backspace') {
      setPasswordInput(prev => prev.slice(0, -1));
    } else if (e.key.length === 1) {
      setPasswordInput(prev => prev + e.key);
    }
  };

  const handleClose = () => {
    setShowPasswordDialog(false);
    setSettingsLoaded(false);
    setPasswordInput('');
    setPasswordError('');
    setSettings({});
    setError('');
    onClose();
  };

  const fetchSettings = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetchDataFromApi('/api/admin/settings');
      if (response && response.success) {
        const settingsMap = {};
        response.data.forEach(setting => {
          settingsMap[setting.settingKey] = setting;
        });
        setSettings(settingsMap);
        setSettingsLoaded(true);
      } else {
        const errorMessage = response?.error || response?.message || "Error loading settings";
        setError(errorMessage);
        context.setAlertBox({
          open: true,
          error: true,
          msg: errorMessage
        });
      }
    } catch (error) {
      const errorMessage = error.message || "Network error loading settings";
      setError(errorMessage);
      context.setAlertBox({
        open: true,
        error: true,
        msg: errorMessage
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSettingChange = async (settingKey, newValue) => {
    setSaving(true);
    setError('');
    try {
      const response = await editData(`/api/admin/settings/${settingKey}`, {
        settingValue: newValue
      });
      if (!response) throw new Error('API returned null/undefined response');

      if (response.success === true || response?.data || response?.id || response?.updated || typeof response === 'boolean' || typeof response === 'string' || typeof response === 'number') {
        setSettings(prev => ({
          ...prev,
          [settingKey]: {
            ...prev[settingKey],
            settingValue: newValue
          }
        }));
        context.setAlertBox({
          open: true,
          error: false,
          msg: "Setting updated successfully"
        });
        return;
      }

      if (response.success === false) {
        const errorMsg = response.error || response.message || 'Setting update failed';
        throw new Error(errorMsg);
      }

      throw new Error(`Unexpected API response format: ${JSON.stringify(response)}`);
    } catch (error) {
      const errorMessage = error.message || "Failed to update setting";
      setError(errorMessage);
      context.setAlertBox({
        open: true,
        error: true,
        msg: errorMessage
      });
    } finally {
      setSaving(false);
    }
  };

  const clientVisibilitySetting = settings['client_visibility_toggle_enabled'];
  const bulkSelectSetting = settings['bulk_product_select_enabled'];

  return (
    <>
      {/* Password Dialog */}
      <Dialog
        open={showPasswordDialog}
        onClose={handleClose}
        fullWidth
        maxWidth="xs"
        disableEscapeKeyDown
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <SettingsIcon />
            Magic Password
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box
            tabIndex={0}
            onKeyDown={handleKeyDown}
            ref={inputRef}
            sx={{
              border: '1px solid #ccc',
              borderRadius: '4px',
              padding: 1.5,
              mt: 1,
              mb: 1,
              minHeight: 56,
              cursor: 'text',
              outline: 'none',
              backgroundColor: '#f5f5f5',
              '&:focus': {
                borderColor: 'primary.main'
              }
            }}
            onClick={() => inputRef.current?.focus()}
          >
            {/* NOTHING IS RENDERED HERE (linux-style) */}
          </Box>

          {passwordError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {passwordError}
            </Alert>
          )}

          <Typography variant="caption" color="text.secondary">
            Type your password and press Enter.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog
        open={open && !showPasswordDialog && settingsLoaded}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <SettingsIcon />
            Magic 
          </Box>
        </DialogTitle>

        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Error:</strong> {error}
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Check browser console for detailed logs
              </Typography>
            </Alert>
          )}

          {loading ? (
            <Box display="flex" alignItems="center" gap={2} py={3}>
              <CircularProgress size={20} />
              <Typography>Loading settings...</Typography>
            </Box>
          ) : (
            <Box>
              <Typography variant="h6" gutterBottom>
                Magic 
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {clientVisibilitySetting ? (
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(clientVisibilitySetting?.settingValue)}
                      onChange={(e) =>
                        handleSettingChange('client_visibility_toggle_enabled', e.target.checked)
                      }
                      disabled={saving}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1">
                        {clientVisibilitySetting?.settingName || 'XYZ'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {clientVisibilitySetting?.settingDescription ||
                          'Just Magic'}
                      </Typography>
                    </Box>
                  }
                />
              ) : (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Client visibility setting not found. Please check your database.
                </Alert>
              )}

              <Divider sx={{ my: 2 }} />

              {bulkSelectSetting ? (
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(bulkSelectSetting?.settingValue)}
                      onChange={(e) =>
                        handleSettingChange('bulk_product_select_enabled', e.target.checked)
                      }
                      disabled={saving}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1">
                        {bulkSelectSetting?.settingName || 'Bulk Product Select & Delete'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {bulkSelectSetting?.settingDescription ||
                          'Show checkboxes on the Product List page for selecting multiple products and deleting them at once.'}
                      </Typography>
                    </Box>
                  }
                />
              ) : (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  Bulk select setting not found. Please check your database.
                </Alert>
              )}

              {saving && (
                <Box display="flex" alignItems="center" gap={1} sx={{ mt: 2 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2">Updating settings...</Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} variant="outlined">
            Close
          </Button>
          <Button
            onClick={() => fetchSettings()}
            variant="outlined"
            disabled={loading}
            sx={{ mr: 1 }}
          >
            Refresh
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loading Dialog */}
      <Dialog
        open={open && !showPasswordDialog && !settingsLoaded && loading}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent>
          <Box display="flex" alignItems="center" gap={2} py={2}>
            <CircularProgress size={24} />
            <Typography>Loading admin settings...</Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AdminSettings;
